import { useQuery } from 'react-query';
import { Moment } from 'moment';
import rota from '../../network/rota';

const DAY_FORMAT = 'YYYY-MM-DD';

/** Rota schedules cached per visible range: ['get_rotas', from, to]. */
export const rotaQueryKey = (from: string, to: string) =>
  ['get_rotas', from, to] as const;

// The API answers 404 for a day that has no schedules.
const isNoSchedules = (error: any) => error?.response?.status === 404;

/**
 * The API only serves schedules one day at a time (`/v1/rota/schedule/:date`;
 * its `schedules/query` range filter is not usable yet), so each visible day
 * is fetched. Empty days are skipped; any other failure fails the query so
 * the screen can show it instead of an empty rota.
 */
async function fetchRotaRange(days: string[]) {
  const results = await Promise.allSettled(
    days.map((day) => rota.getRotaSchedule(day)),
  );

  const failure = results.find(
    (result): result is PromiseRejectedResult =>
      result.status === 'rejected' && !isNoSchedules(result.reason),
  );
  if (failure) {
    throw failure.reason;
  }

  return results
    .filter(
      (result): result is PromiseFulfilledResult<any> =>
        result.status === 'fulfilled',
    )
    .map((result) => result.value);
}

export function useGetRotas(daysMap: Moment[]) {
  const days = daysMap.map((day) => day.format(DAY_FORMAT));
  const from = days[0] ?? '';
  const to = days[days.length - 1] ?? '';

  return useQuery({
    queryKey: rotaQueryKey(from, to),
    queryFn: () => fetchRotaRange(days),
    enabled: days.length > 0,
    retry: false,
    onError: (error) => {
      console.error('Error fetching rotas:', error);
    },
  });
}
